import type { ReactNode } from "react";
import cn from "classnames";
import type { LoadStatus } from "hooks/useProfile";

type DashboardCardProps = {
  title: string;
  /** The grey line under the title. Left out while the card is still loading. */
  sub?: string;
  /** Two columns of the three-column grid, as the design lays the card out. */
  wide?: boolean;
  /** The shortcut in the card's top right, if it has one. */
  action?: { label: string; onClick: () => void };
  status: LoadStatus;
  /** What the card says once it loaded and had nothing to show. */
  empty?: string;
  isEmpty?: boolean;
  /** What the card says when the load failed. Named for the thing that failed. */
  failure: string;
  children: ReactNode;
};

/**
 * The card frame every dashboard card shares, along with its four states.
 *
 * A card that cannot load says so on its own rather than taking the page down
 * with it: one failing request should cost one card, not the dashboard.
 */
export const DashboardCard = ({
  title,
  sub,
  wide = false,
  action,
  status,
  empty,
  isEmpty = false,
  failure,
  children,
}: DashboardCardProps) => (
  <section className={cn("pcx", "dsh__c", { "dsh__c--wide": wide })}>
    <div className="pcx__top">
      <div>
        <div className="pcx__h">{title}</div>
        {status === "ready" && sub !== undefined && <p className="pcx__sub">{sub}</p>}
      </div>

      {action && status === "ready" && !isEmpty && (
        <button type="button" className="pcbtn" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>

    {status === "loading" && <span className="pc-spin" aria-label="Loading" />}

    {status === "failed" && <p className="pc-note pc-note--bad">{failure}</p>}

    {status === "unverified" && <p className="pc-note">Verify your email address to see this.</p>}

    {status === "ready" && (isEmpty ? <p className="pc-note">{empty}</p> : children)}
  </section>
);
